import {
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography
} from '@material-ui/core'
import Compressor from 'compressorjs'
import { SendHorizontal, Upload, X, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { useStyles } from '../UploadDropZone/style' // Verifique se este caminho está correto, parece ser de um componente irmão

import { getFileIconSrc } from '../../utils/fileIconMapper'

export const UploadChatDropZone = ({
  chatId,
  chatStatus = 'open',
  isGroupChat = false,
  children,
  fileInputRef,
  onFileUploadComplete
}) => {
  const classes = useStyles()
  const [isDragging, setIsDragging] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState({})
  const [isUploading, setIsUploading] = useState(false)

  const internalFileInputRef = useRef(null)
  const dropAreaRef = useRef(null)
  const previewContainerRef = useRef(null)

  const inputRef = fileInputRef || internalFileInputRef

  useEffect(() => {
    const handleWheel = e => {
      if (previewContainerRef.current) {
        e.preventDefault()
        previewContainerRef.current.scrollLeft += e.deltaY
      }
    }

    const container = previewContainerRef.current
    if (container) {
      container.removeEventListener('wheel', handleWheel)
      container.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel)
      }
    }
  }, [uploadFiles])

  const containsFiles = e => {
    if (!e.dataTransfer || !e.dataTransfer.types) return false
    return Array.from(e.dataTransfer.types).includes('Files')
  }

  const handleDragEnter = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containsFiles(e)) return
    setIsDragging(true)
  }

  const handleDragOver = e => {
    e.preventDefault()
    e.stopPropagation()
    if (!containsFiles(e)) return
    if (!isDragging) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = e => {
    e.preventDefault()
    e.stopPropagation()

    if (!dropAreaRef.current.contains(e.relatedTarget)) {
      setIsDragging(false)
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    e.stopPropagation()

    setIsDragging(false)

    if (!containsFiles(e)) return

    if (chatStatus === 'closed') {
      toastError(
        new Error('Chat está fechado. Não é possível enviar arquivos.')
      )
      return
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files)
      processFiles(droppedFiles)
    }
  }

  const removeFile = fileId => {
    setUploadFiles(prev => prev.filter(file => file.id !== fileId))
  }

  const removeAllFiles = () => {
    setUploadFiles([])
    setUploadProgress({})
  }

  const uploadFilesToServer = async () => {
    if (chatStatus === 'closed') {
      toastError(
        new Error('Chat está fechado. Não é possível enviar arquivos.')
      )
      return
    }

    if (!chatId) {
      toastError(
        new Error('Chat não encontrado. Não é possível enviar arquivos.')
      )
      return
    }

    setIsUploading(true)

    const filesToUpload = [...uploadFiles]

    for (const fileItem of filesToUpload) {
      if (fileItem.status !== 'pending') {
        continue
      }

      setUploadFiles(prev =>
        prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'uploading' } : f
        )
      )

      const formData = new FormData()
      formData.append('fromMe', true)

      try {
        if (fileItem.file.type.startsWith('image/')) {
          await new Promise(resolve => {
            new Compressor(fileItem.file, {
              quality: 0.7,
              success(compressedFile) {
                formData.append('medias', compressedFile)
                formData.append('body', fileItem.name)
                resolve()
              },
              error(err) {
                console.error(`Error compressing image ${fileItem.name}:`, err) // Mantém este console.error pois é uma exceção
                // Se a compressão falhar, usar arquivo original
                formData.append('medias', fileItem.file)
                formData.append('body', fileItem.name)
                resolve()
              }
            })
          })
        } else {
          formData.append('medias', fileItem.file)
          formData.append('body', fileItem.name)
        }

        await api.post(`/chats/${chatId}/messages`, formData, {
          onUploadProgress: event => {
            const progress = Math.round((event.loaded * 100) / event.total)
            setUploadProgress(prev => ({
              ...prev,
              [fileItem.id]: progress
            }))
            setUploadFiles(prev =>
              prev.map(f =>
                f.id === fileItem.id ? { ...f, progress: progress } : f
              )
            )
          }
        })

        setUploadFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id ? { ...f, status: 'completed' } : f
          )
        )
      } catch (err) {
        setUploadFiles(prev =>
          prev.map(f => (f.id === fileItem.id ? { ...f, status: 'error' } : f))
        )
        toastError(err)
      }
    }

    setUploadFiles(prev =>
      prev.filter(f => f.status === 'pending' || f.status === 'uploading')
    )
    setUploadProgress({})
    setIsUploading(false)

    if (onFileUploadComplete) {
      onFileUploadComplete()
    }
  }

 const getFileIcon = file => {
    if (file.preview) {
      return (
        <div className={classes.previewImage}>
          <img
            src={file.preview}
            alt={file.name || 'Preview da imagem anexada'}
            onClick={() => removeFile(file.id)}
          />
          {file.status === 'uploading' && (
            <div className={classes.imageLoadingOverlay}>
              <CircularProgress
                size={30}
                variant="determinate"
                value={file.progress}
                className={classes.imageProgress}
              />
              <span className={classes.progressPercentage}>
                {file.progress}%
              </span>
            </div>
          )}
        </div>
      )
    }

  const iconSrc = getFileIconSrc(file.name, file.file?.type)
    const extension = file.name
      ? file.name.split('.').pop().toLowerCase()
      : 'unknown'

    return (
      <div className={classes.fileIconContainer}>
        <div className={classes.fileIcon}>
          <img
            src={iconSrc}
            alt={`${extension} file`}
            className={classes.fileIconImage}
          />
          {file.status === 'uploading' && (
            <div className={classes.fileLoadingOverlay}>
              <CircularProgress
                size={30}
                variant="determinate"
                value={file.progress}
                className={classes.fileProgress}
              />
              <span className={classes.progressPercentage}>
                {file.progress}%
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderFilePreview = () => {
    if (uploadFiles.length === 0) return null

    return (
      <div className={classes.previewContainer} aria-label="Arquivos selecionados para envio">
        <div className={classes.previewContent} ref={previewContainerRef}>
          {uploadFiles.map(fileItem => (
            <div key={fileItem.id} className={classes.previewItem}>
              {getFileIcon(fileItem)}
              <div className={classes.previewInfo}>
                <span className={classes.previewName}>{fileItem.name}</span>
              </div>
              <div className={classes.previewActions}>
                <div className={classes.progressContainer}>
                  {fileItem.status === 'uploading' && (
                    <LinearProgress
                      variant="determinate"
                      value={fileItem.progress}
                    />
                  )}
                  {fileItem.status === 'error' && (
                    <Typography variant="caption" color="error">
                      Erro ao enviar arquivo
                    </Typography>
                  )}
                </div>
                <IconButton
                  className={classes.cancelButton}
                  size="small"
                  onClick={() => removeFile(fileItem.id)}
                  aria-label={`Remover arquivo ${fileItem.name}`}
                >
                  <X />
                </IconButton>
              </div>
            </div>
          ))}
        </div>

        <div className={classes.uploadButtonContainer}>
          <button
            onClick={removeAllFiles}
            className={classes.cancelButtonAllFiles}
            aria-label="Limpar fila de uploads"
          >
            <XCircle />
          </button>
          <span className={classes.filesCount}>{uploadFiles.length}</span>

          {uploadFiles.some(file => file.status === 'pending') && (
            <button
              onClick={uploadFilesToServer}
              disabled={isUploading}
              className={classes.uploadButton}
              style={{
                cursor: isUploading ? 'not-allowed' : 'pointer'
              }}
              aria-label="Confirmar e enviar arquivos selecionados"
            >
              <SendHorizontal />
            </button>
          )}
        </div>
      </div>
    )
  }

  const handleFileSelect = e => {
    if (chatStatus === 'closed') {
      toastError(
        new Error('Chat está fechado. Não é possível enviar arquivos.')
      )
      return
    }

    if (!e.target.files || e.target.files.length === 0) {
      return
    }

    const selectedFiles = Array.from(e.target.files)
    processFiles(selectedFiles)

    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handlePaste = e => {
    if (chatStatus === 'closed') {
      return
    }

    e.preventDefault()

    const clipboardData = e.clipboardData

    if (clipboardData.files && clipboardData.files.length > 0) {
      const pastedFiles = Array.from(clipboardData.files)
      processFiles(pastedFiles)
      return
    }

    if (clipboardData.items && clipboardData.items.length > 0) {
      const items = Array.from(clipboardData.items)

      const imageItem = items.find(item => item.type.indexOf('image') !== -1)

      if (imageItem) {
        const blob = imageItem.getAsFile()

        if (blob) {
          processFiles([blob])
          return
        }
      }
    }
    return false
  }

  const processFiles = files => {
    const newFiles = files.map(file => ({
      file,
      id: Math.random().toString(36).substring(2, 9),
      name:
        file.name === 'blob'
          ? `imagem-${new Date().getTime()}.png`
          : file.name || `imagem-${new Date().getTime()}.png`,
      progress: 0,
      status: 'pending',
      preview: null
    }))

    newFiles.forEach(fileItem => {
      if (fileItem.file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = e => {
          setUploadFiles(prevFiles => {
            const updatedFiles = prevFiles.map(f =>
              f.id === fileItem.id ? { ...f, preview: e.target.result } : f
            )
            return updatedFiles
          })
        }
        reader.readAsDataURL(fileItem.file)
      }
    })

    setUploadFiles(prev => {
      const updatedFiles = [...prev, ...newFiles]
      return updatedFiles
    })
  }

  const renderFileInput = () => {
    const isDisabled = chatStatus === 'closed'

    return (
      <>
        <input
          multiple
          type="file"
          id="chat-upload-button"
          ref={inputRef}
          disabled={isDisabled}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </>
    )
  }

  return (
    <div
      className={classes.dropZoneWrapper}
      ref={dropAreaRef}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
      role="region"
      aria-label="Área de transferência de arquivos de chat"
    >
      {renderFileInput()}
      {children}

      {isDragging && (
        <div className={classes.dropZoneOverlay}>
          <div className={classes.dropZoneContent}>
            <div className={classes.dropZoneIcon}>
              <Upload size={48} />
            </div>
            <Typography variant="h6" className={classes.dropZoneText}>
              Solte os arquivos aqui para fazer upload
            </Typography>
          </div>
        </div>
      )}

      {renderFilePreview()}
    </div>
  )
}
